import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/hooks/use-toast';
import {
  useSaveXeroSettings,
  useXeroSettingsOptions,
  XERO_PAYMENT_METHODS,
  type XeroAccountOption,
  type XeroPaymentMethod,
} from '@/hooks/useXeroConnection';

const NONE = '__none__';

const schema = z.object({
  sales_account_code: z.string().min(1, 'Choose the sales account'),
  tax_type_taxable: z.string().min(1, 'Choose the tax rate for taxable lines'),
  tax_type_exempt: z.string().min(1, 'Choose the tax rate for tax-free lines'),
  payment_accounts: z.object({
    cash: z.string().min(1, 'Required'),
    card: z.string().min(1, 'Required'),
    bank_transfer: z.string().min(1, 'Required'),
    other: z.string().min(1, 'Required'),
    square: z.string().min(1, 'Required'),
  }),
  branding_theme_id: z.string(),
  push_status: z.enum(['AUTHORISED', 'DRAFT']),
  pull_contacts: z.boolean(),
});
type FormValues = z.infer<typeof schema>;

const METHOD_LABELS: Record<XeroPaymentMethod, string> = {
  cash: 'Cash',
  card: 'Card / EFTPOS',
  bank_transfer: 'Bank transfer',
  other: 'Other',
  square: 'Square (online payments)',
};

const REVENUE_TYPES = new Set(['REVENUE', 'SALES', 'OTHERINCOME']);

function accountLabel(a: XeroAccountOption): string {
  return a.code ? `${a.code} · ${a.name}` : a.name;
}

interface XeroMappingFormProps {
  enabled: boolean;
  onSaved?: (setupComplete: boolean) => void;
  /** Fires when the saved mapping is loaded, with whether it is complete. */
  onLoaded?: (setupComplete: boolean) => void;
}

/**
 * Which Xero accounts and tax rates Steddy documents land on. Options come
 * live from the organisation's chart of accounts; the save is validated
 * against it again server-side.
 */
export function XeroMappingForm({ enabled, onSaved, onLoaded }: XeroMappingFormProps) {
  const options = useXeroSettingsOptions(enabled);
  const save = useSaveXeroSettings();

  useEffect(() => {
    if (options.data) onLoaded?.(options.data.settings?.setup_complete ?? false);
  }, [options.data, onLoaded]);

  const catalog = options.data?.catalog;
  const revenueAccounts = useMemo(
    () => (catalog?.accounts ?? []).filter((a) => REVENUE_TYPES.has(a.type) && a.code),
    [catalog],
  );
  // Xero lets a payment land on a bank account or on any account with
  // "enable payments" ticked. It ticks that by default on equity accounts like
  // Owner Drawings, so the two groups are kept apart and banked separately:
  // customer money belongs in a bank account, and offering the rest without a
  // label invites a genuine accounting mistake.
  const bankAccounts = useMemo(
    () => (catalog?.accounts ?? []).filter((a) => a.type === 'BANK' && a.code),
    [catalog],
  );
  const otherPayableAccounts = useMemo(
    () => (catalog?.accounts ?? []).filter((a) => a.type !== 'BANK' && a.enable_payments && a.code),
    [catalog],
  );
  const payableAccounts = useMemo(
    () => [...bankAccounts, ...otherPayableAccounts],
    [bankAccounts, otherPayableAccounts],
  );
  // Sales lines can only carry a tax rate Xero allows on income. Offering
  // expense rates such as "GST on Expenses" gets the whole invoice rejected.
  const taxRates = useMemo(
    () => (catalog?.tax_rates ?? []).filter((t) => t.can_apply_to_revenue),
    [catalog],
  );
  const themes = catalog?.branding_themes ?? [];

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      sales_account_code: '',
      tax_type_taxable: '',
      tax_type_exempt: '',
      payment_accounts: { cash: '', card: '', bank_transfer: '', other: '', square: '' },
      branding_theme_id: NONE,
      push_status: 'AUTHORISED',
      pull_contacts: true,
    },
  });

  // Prefill from the saved row, else from the per-country suggestion.
  useEffect(() => {
    if (!options.data) return;
    const s = options.data.settings;
    const sug = options.data.suggested;
    const squareClearing = payableAccounts.find((a) => /square|clearing/i.test(a.name))?.code ?? null;
    const fallbackBank = sug.payment_account ?? bankAccounts[0]?.code ?? '';
    const pa = s?.payment_accounts ?? {};
    form.reset({
      sales_account_code: s?.sales_account_code ?? sug.sales_account_code ?? '',
      tax_type_taxable: s?.tax_type_taxable ?? sug.tax_type_taxable ?? '',
      tax_type_exempt: s?.tax_type_exempt ?? sug.tax_type_exempt ?? '',
      payment_accounts: {
        cash: pa.cash ?? fallbackBank,
        card: pa.card ?? fallbackBank,
        bank_transfer: pa.bank_transfer ?? fallbackBank,
        other: pa.other ?? fallbackBank,
        square: pa.square ?? squareClearing ?? fallbackBank,
      },
      branding_theme_id: s?.branding_theme_id ?? NONE,
      push_status: s?.push_status ?? 'AUTHORISED',
      pull_contacts: s?.pull_contacts ?? true,
    });
  }, [options.data, payableAccounts, form]);

  const onSubmit = async (values: FormValues) => {
    try {
      const result = await save.mutateAsync({
        sales_account_code: values.sales_account_code,
        tax_type_taxable: values.tax_type_taxable,
        tax_type_exempt: values.tax_type_exempt,
        payment_accounts: values.payment_accounts,
        branding_theme_id: values.branding_theme_id === NONE ? null : values.branding_theme_id,
        push_status: values.push_status,
        pull_contacts: values.pull_contacts,
      });
      toast({
        title: 'Xero mapping saved',
        description: result.setup_complete ? 'New invoices and payments will now sync to Xero.' : 'Some fields are still missing.',
      });
      onSaved?.(result.setup_complete);
    } catch (e) {
      toast({ title: 'Could not save Xero mapping', description: e instanceof Error ? e.message : 'Unknown error', variant: 'destructive' });
    }
  };

  if (!enabled) return null;

  if (options.isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading your chart of accounts from Xero…
      </div>
    );
  }
  if (options.isError) {
    return (
      <div className="space-y-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm">
        <div className="font-medium">Could not load accounts from Xero</div>
        <div className="text-muted-foreground">{options.error instanceof Error ? options.error.message : 'Unknown error'}</div>
        <Button size="sm" variant="outline" onClick={() => void options.refetch()}>
          Try again
        </Button>
      </div>
    );
  }
  if (!options.data) return null;

  const accountSelect = (value: string, onChange: (v: string) => void, list: XeroAccountOption[], placeholder: string) => (
    <Select value={value || undefined} onValueChange={onChange}>
      <FormControl>
        <SelectTrigger>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
      </FormControl>
      <SelectContent>
        {list.map((a) => (
          <SelectItem key={a.account_id} value={a.code!}>
            {accountLabel(a)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const paymentAccountSelect = (value: string, onChange: (v: string) => void) => (
    <Select value={value || undefined} onValueChange={onChange}>
      <FormControl>
        <SelectTrigger>
          <SelectValue placeholder={bankAccounts.length > 0 ? 'Choose a bank account' : 'No bank account in Xero yet'} />
        </SelectTrigger>
      </FormControl>
      <SelectContent>
        {bankAccounts.length > 0 && (
          <SelectGroup>
            <SelectLabel>Bank accounts</SelectLabel>
            {bankAccounts.map((a) => (
              <SelectItem key={a.account_id} value={a.code!}>
                {accountLabel(a)}
              </SelectItem>
            ))}
          </SelectGroup>
        )}
        {otherPayableAccounts.length > 0 && (
          <SelectGroup>
            <SelectLabel>Other accounts that accept payments</SelectLabel>
            {otherPayableAccounts.map((a) => (
              <SelectItem key={a.account_id} value={a.code!}>
                {accountLabel(a)}
              </SelectItem>
            ))}
          </SelectGroup>
        )}
      </SelectContent>
    </Select>
  );

  const taxSelect = (value: string, onChange: (v: string) => void, placeholder: string) => (
    <Select value={value || undefined} onValueChange={onChange}>
      <FormControl>
        <SelectTrigger>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
      </FormControl>
      <SelectContent>
        {taxRates.map((t) => (
          <SelectItem key={t.tax_type} value={t.tax_type}>
            {t.name} ({t.effective_rate}%)
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">Where things land in Xero</p>
          <Button type="button" variant="ghost" size="sm" onClick={() => void options.refetch()} disabled={options.isFetching}>
            <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${options.isFetching ? 'animate-spin' : ''}`} />
            Refresh accounts
          </Button>
        </div>

        <FormField
          control={form.control}
          name="sales_account_code"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Sales account</FormLabel>
              {accountSelect(field.value, field.onChange, revenueAccounts, 'Choose a revenue account')}
              <FormDescription>Every invoice line is coded here.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="tax_type_taxable"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tax rate on taxable lines</FormLabel>
                {taxSelect(field.value, field.onChange, 'Choose a tax rate')}
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="tax_type_exempt"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tax rate on tax-free lines</FormLabel>
                {taxSelect(field.value, field.onChange, 'Choose a tax rate')}
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">Where payments are banked</p>
          {bankAccounts.length === 0 && (
            <div className="flex gap-2 rounded-lg border border-warning/40 bg-warning/5 p-3 text-sm">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
              <div>
                <div className="font-medium">This Xero organisation has no bank accounts</div>
                <div className="text-muted-foreground">
                  Add one in Xero under Accounting, then Bank accounts, then use Refresh accounts above. The accounts
                  offered below are equity and expense accounts that merely allow payments, such as Owner Drawings, and
                  customer money should not land there.
                </div>
              </div>
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            {XERO_PAYMENT_METHODS.map((method) => (
              <FormField
                key={method}
                control={form.control}
                name={`payment_accounts.${method}` as const}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{METHOD_LABELS[method]}</FormLabel>
                    {paymentAccountSelect(field.value, field.onChange)}
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            Only bank accounts and accounts with “enable payments” turned on in Xero are listed. Square payouts
            usually go to a clearing account.
          </p>
        </div>

        <FormField
          control={form.control}
          name="branding_theme_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Branding theme (optional)</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Xero default" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value={NONE}>Xero default</SelectItem>
                  {themes.map((t) => (
                    <SelectItem key={t.branding_theme_id} value={t.branding_theme_id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="push_status"
          render={({ field }) => (
            <FormItem className="flex items-center justify-between gap-4 rounded-lg border p-3">
              <div className="space-y-0.5">
                <FormLabel>Send invoices as drafts</FormLabel>
                <FormDescription>
                  Off: invoices arrive approved and payments sync straight away. On: your bookkeeper approves each
                  invoice in Xero first, and payments wait until they do.
                </FormDescription>
              </div>
              <FormControl>
                <Switch checked={field.value === 'DRAFT'} onCheckedChange={(v) => field.onChange(v ? 'DRAFT' : 'AUTHORISED')} />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="pull_contacts"
          render={({ field }) => (
            <FormItem className="flex items-center justify-between gap-4 rounded-lg border p-3">
              <div className="space-y-0.5">
                <FormLabel>Bring contact edits back from Xero</FormLabel>
                <FormDescription>Name, phone, email and address changes made in Xero update the customer here.</FormDescription>
              </div>
              <FormControl>
                <Switch checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="flex justify-end">
          <Button type="submit" disabled={save.isPending}>
            {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save mapping
          </Button>
        </div>
      </form>
    </Form>
  );
}
